import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Box } from '@mui/material'
import React from 'react'

import OrganizeSectionRow from './OrganizeSectionRow'

interface SortableSectionRowProps {
  sectionKey: string
  displayText: string
  ancestorPath: string
  isSelected: boolean
  isDraggedAway: boolean
  isDimmed?: boolean
  isPickedFrom?: boolean
  hasViolation?: boolean
  relationToFocus?: 'before' | 'after'
  /** When true, sortable listeners are skipped so clicks read as selection-only (deps mode). */
  disableDrag?: boolean
  onSelect: (key: string, shiftKey: boolean, metaKey: boolean) => void
  onPreview?: (key: string) => void
  onHoverChange?: (key: string | null) => void
  bookFontFamily?: string
  bookDirection?: 'ltr' | 'rtl'
  tagRail?: React.ReactNode
  dependencyChips?: React.ReactNode
}

export default function SortableSectionRow({
  sectionKey,
  displayText,
  ancestorPath,
  isSelected,
  isDraggedAway,
  isDimmed,
  isPickedFrom,
  hasViolation,
  relationToFocus,
  disableDrag,
  onSelect,
  onPreview,
  onHoverChange,
  bookFontFamily,
  bookDirection,
  tagRail,
  dependencyChips,
}: SortableSectionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sectionKey,
    disabled: disableDrag,
  })

  // touchAction: 'manipulation' (not 'none') so the dialog still scrolls on
  // mobile. The TouchSensor's delay activation gates drag behind a long-press,
  // and during the delay window the browser is free to handle scrolling — a
  // movement > tolerance cancels activation before dnd-kit takes over.
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isDraggedAway ? 0.3 : 1,
    outline: 'none',
    touchAction: 'manipulation',
  }

  const dragProps = disableDrag ? {} : { ...attributes, ...listeners }

  return (
    <div ref={setNodeRef} style={style} data-section-key={sectionKey} {...dragProps}>
      <Box
        onClick={(e) => {
          e.stopPropagation()
          onSelect(sectionKey, e.shiftKey, e.metaKey || e.ctrlKey)
        }}
        onMouseEnter={onHoverChange ? () => onHoverChange(sectionKey) : undefined}
        onMouseLeave={onHoverChange ? () => onHoverChange(null) : undefined}
      >
        <OrganizeSectionRow
          displayText={displayText}
          ancestorPath={ancestorPath}
          isSelected={isSelected}
          isDimmed={isDimmed}
          isPickedFrom={isPickedFrom}
          hasViolation={hasViolation}
          relationToFocus={relationToFocus}
          bookFontFamily={bookFontFamily}
          bookDirection={bookDirection}
          onPreview={onPreview ? () => onPreview(sectionKey) : undefined}
          tagRail={tagRail}
          dependencyChips={dependencyChips}
        />
      </Box>
    </div>
  )
}
